import { faCheck, faCopy } from '@fortawesome/pro-regular-svg-icons';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Button from '../../../core/components/Button.tsx';
import { withIcon } from '../../../core/components/Icon.tsx';

const CopyIcon = withIcon(faCopy);
const CheckIcon = withIcon(faCheck);

const COPIED_RESET_MS = 2000;

interface ConsentLinkRowProps {
    link: string;
}

/** An admin consent link with a copy button that confirms for a moment. */
const ConsentLinkRow = ({ link }: ConsentLinkRowProps) => {
    const { t } = useTranslation('mailboxes');
    const [isCopied, setCopied] = useState(false);
    const copiedResetRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

    useEffect(() => () => clearTimeout(copiedResetRef.current), []);

    const copyLink = async () => {
        try {
            await navigator.clipboard.writeText(link);
            setCopied(true);
            clearTimeout(copiedResetRef.current);
            copiedResetRef.current = setTimeout(() => setCopied(false), COPIED_RESET_MS);
        } catch (error) {
            console.error('Failed to copy link:', error);
        }
    };

    return (
        <div className="flex items-center gap-2">
            <span className="min-w-0 flex-1 truncate rounded bg-surface-medium px-3 py-2 text-sm text-content-strong">
                {link}
            </span>
            <Button
                variant="ghost"
                onClick={copyLink}
                disabled={!link}
                className="flex flex-none items-center gap-1.5 px-2 py-1 h-auto text-xs cursor-pointer"
            >
                {isCopied ? (
                    <>
                        <CheckIcon />
                        <span className="font-semibold">{t('overview.setup.copied')}</span>
                    </>
                ) : (
                    <>
                        <CopyIcon />
                        {t('overview.setup.copyLink')}
                    </>
                )}
            </Button>
        </div>
    );
};

export default ConsentLinkRow;
